import { type FC, type ReactElement, useCallback } from 'react';

import classNames from 'classnames';

import { DEFAULT_DATE_TIME_FORMAT } from '@iconik-io/core/src/date/date_formats';
import {
  formatLocalDate,
  formatRelativeDate,
} from '@iconik-io/core/src/date/format';
import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import type { AssetsSegmentSchemaWithIdPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import { httpPatchAssetsByAssetIdSegmentsBySegmentId } from '@iconik-io/iconik-api/src/services/assets';
import {
  Chip,
  Focusable,
  Icon,
  ToggleButton,
  Tooltip,
  TooltipTrigger,
  useCanHover,
} from '@iconik-io/uad';

import { selectUserInfoBySegmentId } from '../../../../redux/entities/selectors/segments';
import { useAppDispatch, useAppSelector } from '../../../../redux/redux_hooks';
import { useEntityBatch } from '../../../hooks/entity/use_entity_batch';
import { SegmentActions } from './segment_actions';
import { SegmentHeaderUser } from './segment_header_user';

import styles from './segment_header.module.css';

type Props = {
  segment: AssetsSegmentSchemaWithIdPatched;
  versionName?: string;
  onEdit: () => void;
  editButtonDisabled?: boolean;
  editButtonPrependElement?: ReactElement;
  isEditActive?: boolean;
  onDelete: () => void;
  showActions: boolean;
  canEdit: boolean;
  canDelete: boolean;
  assetId: string;
  canApproveComments?: boolean;
  canMarkCommentComplete?: boolean;
  onMarkCompleteBlocked?: () => void;
};

export const SegmentHeader: FC<Props> = ({
  segment,
  versionName,
  onEdit,
  editButtonDisabled,
  editButtonPrependElement,
  isEditActive,
  onDelete,
  showActions,
  canEdit,
  canDelete,
  assetId,
  canApproveComments = true,
  canMarkCommentComplete = true,
  onMarkCompleteBlocked,
}) => {
  const dispatch = useAppDispatch();
  const hasDeviceHoverSupport = useCanHover();
  const actionElementClassnames = classNames(styles.checkbox_checked, {
    [styles.checkbox_not_checked]: !showActions,
  });

  const handleCheckPress = useCallback(() => {
    if (!canMarkCommentComplete) {
      onMarkCompleteBlocked?.();
      return;
    }
    dispatch(
      httpPatchAssetsByAssetIdSegmentsBySegmentId(
        segment.asset_id,
        segment.id,
        { segment_checked: !segment.segment_checked },
        {
          index_immediately: 'true',
        },
      ),
    );
  }, [
    canMarkCommentComplete,
    onMarkCompleteBlocked,
    segment.asset_id,
    segment.id,
    segment.segment_checked,
  ]);

  const userInfo = useAppSelector((state) =>
    selectUserInfoBySegmentId(state, segment.id),
  );

  // Make sure that user info is loaded.
  // In some cases (e.g. when segment is updated by WebSocket) we might miss
  // user info in the store.
  useEntityBatch({ entityType: 'users_basic', primaryId: segment.user_id });
  const isParentSegment = segment.asset_id !== assetId;
  return (
    <div className={styles.header}>
      <div className={styles.info}>
        {userInfo ? (
          <SegmentHeaderUser
            photoUrl={userInfo?.photo}
            userFullName={getUserLabel(userInfo) || 'Unknown user'}
          />
        ) : null}
        {versionName ? (
          <Chip size="xs" className={styles.version_chip} truncate>
            {versionName}
          </Chip>
        ) : null}
        {isParentSegment ? (
          <TooltipTrigger>
            <Focusable>
              <Chip variant="badge">P</Chip>
            </Focusable>
            <Tooltip>Comment is made on a parent asset</Tooltip>
          </TooltipTrigger>
        ) : null}
        {segment.date_created ? (
          <time
            className={styles.date}
            dateTime={segment.date_created}
            title={
              formatLocalDate(segment.date_created, DEFAULT_DATE_TIME_FORMAT) ??
              undefined
            }
          >
            {formatRelativeDate(segment.date_created)}
          </time>
        ) : null}
      </div>
      <div className={styles.actions}>
        <SegmentActions
          isEditActive={isEditActive}
          editButtonDisabled={editButtonDisabled}
          showActions={showActions}
          segment={segment}
          canEdit={canEdit}
          canDelete={canDelete}
          onDelete={onDelete}
          editButtonPrependElement={editButtonPrependElement}
          onEdit={onEdit}
        />
        {canApproveComments ? (
          <TooltipTrigger>
            <ToggleButton
              aria-label="Mark comment as completed"
              size="s"
              prominent
              variant="stroke"
              isSelected={segment.segment_checked}
              icon={<Icon name="checkmark" />}
              onPress={handleCheckPress}
              className={classNames(styles.checkbox_checked, {
                [actionElementClassnames]:
                  !segment.segment_checked &&
                  hasDeviceHoverSupport &&
                  !isEditActive,
              })}
            />
            {!segment.segment_checked ? (
              <Tooltip>Mark comment as completed</Tooltip>
            ) : null}
          </TooltipTrigger>
        ) : null}
      </div>
    </div>
  );
};
